import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, ApiClientError } from '@client/lib/api';
import { formatCents } from '@shared/money';
import styles from './QuotesPage.module.css';

/** A row in the quotes list, as returned by GET /api/v1/quotes. */
interface QuoteRow {
  id: string;
  quoteNumber: string;
  customer: { id: string; name: string } | null;
  issueDate: string;
  totalCents: number;
  status: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'empty' }
  | { kind: 'loaded'; quotes: QuoteRow[] };

/**
 * Quotes list screen.
 *
 * Money invariant: every figure shown comes straight from the server. The
 * client never sums, never derives the balance, never recomputes tax.
 */
export function QuotesPage() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const load = () => {
    setState({ kind: 'loading' });
    apiGet<QuoteRow[]>('/api/v1/quotes')
      .then((quotes) => {
        if (quotes.length === 0) {
          setState({ kind: 'empty' });
        } else {
          setState({ kind: 'loaded', quotes });
        }
      })
      .catch((err) => {
        if (err instanceof ApiClientError) {
          setState({ kind: 'error', message: err.message });
        } else {
          setState({ kind: 'error', message: 'Something went wrong.' });
        }
      });
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <main className={styles.container}>
      <h1 className={styles.title}>Quotes</h1>
      {state.kind === 'loading' && (
        <p className={styles.loading} aria-live="polite">
          Loading quotes…
        </p>
      )}
      {state.kind === 'empty' && <p className={styles.empty}>No quotes yet.</p>}
      {state.kind === 'error' && (
        <div className={styles.error} role="alert">
          <p>{state.message}</p>
          <button type="button" className={styles.retryButton} onClick={() => load()}>
            Retry
          </button>
        </div>
      )}
      {state.kind === 'loaded' && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Quote</th>
              <th>Customer</th>
              <th>Issue date</th>
              <th>Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {state.quotes.map((quote) => (
              <tr key={quote.id}>
                <td>
                  <Link to={`/quotes/${quote.id}`} className={styles.rowLink}>
                    {quote.quoteNumber}
                  </Link>
                </td>
                <td>{quote.customer?.name ?? '—'}</td>
                <td>{quote.issueDate}</td>
                <td>{formatCents(quote.totalCents)}</td>
                <td>{quote.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
